import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import * as rxOp from 'rxjs/operators';
import { catchError, tap } from 'rxjs/operators';
import { Activity, PaginatedList } from '../models';
import { UtilsService } from './utils.service';
import _ from 'lodash';
import { GeoLocation } from '../models/location';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

const US_STATE_CODES: { [key: string]: string } = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA',
  colorado: 'CO', connecticut: 'CT', delaware: 'DE', florida: 'FL', georgia: 'GA',
  hawaii: 'HI', idaho: 'ID', illinois: 'IL', indiana: 'IN', iowa: 'IA',
  kansas: 'KS', kentucky: 'KY', louisiana: 'LA', maine: 'ME', maryland: 'MD',
  massachusetts: 'MA', michigan: 'MI', minnesota: 'MN', mississippi: 'MS', missouri: 'MO',
  montana: 'MT', nebraska: 'NE', nevada: 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ',
  'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', ohio: 'OH',
  oklahoma: 'OK', oregon: 'OR', pennsylvania: 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', tennessee: 'TN', texas: 'TX', utah: 'UT', vermont: 'VT',
  virginia: 'VA', washington: 'WA', 'west virginia': 'WV', wisconsin: 'WI', wyoming: 'WY',
  'district of columbia': 'DC'
};

@Injectable({ providedIn: 'root' })
export class LocationService {
  constructor(private http: HttpClient, private utils: UtilsService) { }

  private toStateCode(state?: string, subdivisionCode?: string): string {
    if (subdivisionCode && subdivisionCode.includes('-')) {
      return subdivisionCode.split('-')[1];
    }
    if (!state) {
      return subdivisionCode || '';
    }
    const trimmed = state.trim();
    if (trimmed.length === 2) {
      return trimmed.toUpperCase();
    }
    return US_STATE_CODES[trimmed.toLowerCase()] || trimmed;
  }

  private parseCoords(coordinates: any, defaultOrder: 'latLng' | 'lngLat' = 'lngLat'): { lat: number; lng: number } | null {
    let a: number;
    let b: number;
    if (Array.isArray(coordinates) && coordinates.length >= 2) {
      a = Number(coordinates[0]);
      b = Number(coordinates[1]);
    } else if (typeof coordinates === 'string' && coordinates.includes(',')) {
      const parts = coordinates.split(',');
      a = Number(parts[0]);
      b = Number(parts[1]);
    } else {
      return null;
    }
    if (isNaN(a) || isNaN(b)) {
      return null;
    }
    // Auto-detect if latitude (>90 or <-90 is definitely longitude)
    if (Math.abs(a) > 90 && Math.abs(b) <= 90) {
      return { lng: a, lat: b };
    }
    if (Math.abs(b) > 90 && Math.abs(a) <= 90) {
      return { lat: a, lng: b };
    }
    return defaultOrder === 'latLng' ? { lat: a, lng: b } : { lng: a, lat: b };
  }

  reverse(long: number, lat: number): Observable<any> {
    const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${encodeURIComponent(lat)}&longitude=${encodeURIComponent(long)}&localityLanguage=en`;
    return this.http.get<any>(url).pipe(
      rxOp.map((resp) => {
        const city = resp?.city || resp?.locality || '';
        const state = this.toStateCode(resp?.principalSubdivision, resp?.principalSubdivisionCode);
        const country = resp?.countryCode || 'US';
        const adminList = resp?.localityInfo?.administrative || [];
        const countyObj = adminList.find((x: any) => /county/i.test(x?.name || x?.description || ''));
        const county = countyObj?.name || '';
        return {
          results: [
            {
              locations: city
                ? [
                    {
                      adminArea5: city,
                      adminArea4: county,
                      adminArea3: state,
                      adminArea1: country,
                      latLng: { lat: Number(lat), lng: Number(long) }
                    }
                  ]
                : []
            }
          ]
        };
      }),
      catchError(this.handleError('reverse', { results: [{ locations: [] }] }))
    );
  }

  getCities(searchStr: string, count?: number, qp?: Object): Observable<any> {
    const limit = count == null ? 10 : count;
    const q = encodeURIComponent((searchStr || '').trim());
    if (!q) {
      return of({ results: [] });
    }
    const url = `https://photon.komoot.io/api/?q=${q}&limit=${limit}&lang=en`;
    return this.http.get<any>(url).pipe(
      rxOp.map((resp) => {
        const features = Array.isArray(resp?.features) ? resp.features : [];
        const seen = new Set<string>();
        const results: any[] = [];
        for (const f of features) {
          const props = f?.properties || {};
          const coords = f?.geometry?.coordinates || [];
          const city = props.city || props.name || props.town || props.village || '';
          const state = this.toStateCode(props.state, props.statecode);
          const country = (props.countrycode || 'US').toUpperCase();
          if (!city) continue;
          const displayName = state ? `${city}, ${state}` : city;
          const dedupeKey = displayName.toLowerCase();
          if (seen.has(dedupeKey)) continue;
          seen.add(dedupeKey);
          const lng = coords[0] != null ? Number(coords[0]) : undefined;
          const lat = coords[1] != null ? Number(coords[1]) : undefined;
          results.push(
            new GeoLocation({
              name: city,
              city,
              state,
              country,
              place: {
                geometry: { coordinates: [lng, lat] },
                properties: {
                  city,
                  stateCode: state,
                  countryCode: country,
                  country
                }
              },
              fields: {
                city,
                state,
                country,
                lng,
                lat
              }
            })
          );
        }
        return { results };
      }),
      catchError(this.handleError('getCities', { results: [] }))
    );
  }

  getNearBy(coordinates: any, searchStr?: string, count?: number, qp?: Object): Observable<any> {
    const limit = count == null ? 10 : count;
    const parsed = this.parseCoords(coordinates, 'lngLat');
    const q = (searchStr || '').trim();
    if (!q && parsed) {
      return this.radius([parsed.lat, parsed.lng], 100, limit).pipe(
        rxOp.map((res: any) => GeoLocation.fromList(res?.searchResults || []))
      );
    }
    const locParams = parsed ? `&lat=${encodeURIComponent(parsed.lat)}&lon=${encodeURIComponent(parsed.lng)}` : '';
    const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(q || 'park')}${locParams}&limit=${limit}&lang=en`;
    return this.http.get<any>(url).pipe(
      rxOp.map((resp) => {
        const features = Array.isArray(resp?.features) ? resp.features : [];
        const mapped = features.map((f: any) => {
          const props = f?.properties || {};
          const coords = f?.geometry?.coordinates || [];
          const city = props.city || props.town || props.village || props.county || props.name || '';
          const state = this.toStateCode(props.state, props.statecode);
          const country = (props.countrycode || 'US').toUpperCase();
          const rawStreet = [props.housenumber, props.street].filter(Boolean).join(' ');
          const title = props.name || rawStreet || city;
          const street = rawStreet || (title && city && title !== city ? city : '');
          const lng = coords[0] != null ? Number(coords[0]) : undefined;
          const lat = coords[1] != null ? Number(coords[1]) : undefined;
          return {
            name: title || city,
            street,
            city,
            state,
            country,
            place: {
              geometry: { coordinates: [lng, lat] },
              properties: {
                street,
                city,
                stateCode: state,
                countryCode: country,
                country
              }
            },
            fields: {
              city,
              state,
              country,
              lng,
              lat
            }
          };
        });
        return GeoLocation.fromList(mapped);
      }),
      catchError(this.handleError('getNearBy', []))
    );
  }

  radius(coordinates: any, distance?: number, count?: number, qp?: Object): Observable<any> {
    const parsed = this.parseCoords(coordinates, 'latLng');
    if (!parsed) {
      return of({ searchResults: [] });
    }
    const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${encodeURIComponent(parsed.lat)}&longitude=${encodeURIComponent(parsed.lng)}&localityLanguage=en`;
    return this.http.get<any>(url).pipe(
      rxOp.map((resp) => {
        const city = resp?.city || resp?.locality || '';
        const state = this.toStateCode(resp?.principalSubdivision, resp?.principalSubdivisionCode);
        const country = resp?.countryCode || 'US';
        if (!city) {
          return { searchResults: [] };
        }
        return {
          searchResults: [
            {
              name: city,
              fields: {
                city,
                state,
                country,
                lat: parsed.lat,
                lng: parsed.lng
              }
            }
          ]
        };
      }),
      catchError(this.handleError('radius', { searchResults: [] }))
    );
  }

  ipInfo(ip: string, qp?: Object): Observable<any> {
    const url = 'https://api.bigdatacloud.net/data/reverse-geocode-client?localityLanguage=en';
    return this.http.get<any>(url).pipe(
      rxOp.map((resp: any) => {
        const lat = resp?.latitude;
        const lng = resp?.longitude;
        return {
          ...resp,
          city: resp?.city || resp?.locality || '',
          region_code: this.toStateCode(resp?.principalSubdivision, resp?.principalSubdivisionCode),
          loc: lat != null && lng != null ? `${lat},${lng}` : undefined
        };
      }),
      catchError(this.handleError('ipInfo', {}))
    );
  }

  getIp(): Observable<any> {
    const url = 'https://api.ipify.org?format=json';
    return this.http.get(url).pipe(
      rxOp.map((resp) => {
        return resp;
      }),
      catchError(this.handleError('getIp', {}))
    );
  }

  //   delete(id: string, qp?: Object): Observable<any> {
  //     const url = this.utils.getUrl('rest:post-detail', id);
  //     return this.http.delete(url, this.utils.requestOptions(qp));
  //   }

  //   create(obj: Object, qp?: Object): Observable<any> {
  //     const url = this.utils.getUrl('rest:post-list');
  //     return this.http
  //       .post(url, obj, this.utils.requestOptions(qp));
  //   }

  //   update(id: number, obj: Object, qp?: Object): Observable<any> {
  //     const url = this.utils.getUrl('rest:post-detail', id);
  //     return this.http
  //       .patch(url, obj, this.utils.requestOptions(qp));
  //   }

  // ERROR LOGGING
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      // TODO: send the error to remote logging infrastructure
      // console.error(error); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  /** Log a StockService message with the MessageService */
  private log(message: string) {
    console.log(message);
  }
}
